/**
 * Toast Container Component
 *
 * Displays toast notifications with auto-dismiss and manual dismiss support.
 * Supports success, error, warning, and info toast types.
 */

import { Toast } from '../hooks/useToast';

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

/** Get icon for toast type */
function ToastIcon({ type }: { type: Toast['type'] }) {
  switch (type) {
    case 'success':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
  }
}

/** Get color scheme for toast type */
function getToastColors(type: Toast['type']): { bg: string; border: string; icon: string; text: string } {
  switch (type) {
    case 'success':
      return {
        bg: '#f0fdf4',
        border: '#22c55e',
        icon: '#16a34a',
        text: '#15803d',
      };
    case 'error':
      return {
        bg: '#fef2f2',
        border: '#ef4444',
        icon: '#dc2626',
        text: '#b91c1c',
      };
    case 'warning':
      return {
        bg: '#fffbeb',
        border: '#f59e0b',
        icon: '#d97706',
        text: '#b45309',
      };
    case 'info':
    default:
      return {
        bg: '#eff6ff',
        border: '#3b82f6',
        icon: '#2563eb',
        text: '#1d4ed8',
      };
  }
}

export default function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => {
        const colors = getToastColors(toast.type);

        return (
          <div
            key={toast.id}
            className="toast-item"
            style={{
              backgroundColor: colors.bg,
              borderLeftColor: colors.border,
            }}
          >
            <div className="toast-icon" style={{ color: colors.icon }}>
              <ToastIcon type={toast.type} />
            </div>

            <div className="toast-content">
              {toast.title && (
                <div className="toast-title" style={{ color: colors.text }}>
                  {toast.title}
                </div>
              )}
              <div className="toast-message">{toast.message}</div>
            </div>

            <button
              className="toast-dismiss"
              onClick={() => onDismiss(toast.id)}
              title="Dismiss"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
